<template>
  <div class="rs-pagination-bar">
    <RsPagination
      :value="boundValue"
      @input="emitRange"
    />
    <div class="pagination-info">
      <select
        v-if="showOptionValues"
        class="custom-select"
        :value="boundValue.recordsPerPage"
        @change="(e) => onRecordsPerPageChange(e)"
      >
        <option value="20">
          20
        </option>
        <option value="40">
          40
        </option>
        <option value="80">
          80
        </option>
      </select>
      <span>{{ $ts('pagination.showing') }} {{ boundValue.currentRange }} {{ $ts('pagination.of') }} {{ boundValue.totalRecords }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { IPageRange } from '@/shared';

export default defineComponent({
  name: 'RsPaginationBar',

  props: {
    showOptionValues: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Object as () => IPageRange,
      default: () => ({
        recordsPerPage: 20,
        totalPages: 0,
        currentPage: 1,
        currentRange: '1 - 20',
        totalRecords: 0,
      } as IPageRange),
    },
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: Object as () => IPageRange,
      default: undefined,
    },
  },

  emits: ['input', 'update:modelValue'],

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): IPageRange {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
  },

  methods: {
    /**
     * Emits a new page range to the parent.
     *
     * Order matters: `update:modelValue` must fire before `input`. Under Vue 2 the two
     * were the same `input` event and Vue merged the v-model writer ahead of the consumer's
     * own `@input` handler, so parents that bind `v-model` *and* `@input` (e.g. the websites
     * list) could read their freshly-updated state inside that handler. In Vue 3 they are
     * separate events, so emitting `input` first would run the consumer's handler against
     * the stale page and leave the list one page behind the pagination control.
     */
    emitRange(range: IPageRange): void {
      this.$emit('update:modelValue', range);
      this.$emit('input', range);
    },

    onRecordsPerPageChange(e: Event): void {
      this.emitRange({
        ...this.boundValue,
        ...{ recordsPerPage: parseInt((e.target as HTMLSelectElement).value, 10), currentPage: 1 },
      });
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
$gray: #8694B3;
.rs-pagination-bar{
  margin-top: 2rem;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .pagination-info{
    display: flex;
    justify-content: space-between;
    align-items: center;
    span{
      margin-left: 0.5rem;
      font-size: 0.875rem;
      color: $tertiary-muted;
    }
    .custom-select{
      padding: 0rem 1.75rem 0rem 0.5rem;
      width: auto;
      height: 32px;
      background-color: rgba($gray, 0.1);
      border: none;
      color: $gray;
      cursor: pointer;
      &:focus{
        box-shadow: none;
      }
      option{
        cursor: pointer;
      }
    }
  }
}
</style>
